<template>
  <div class="reg-root">
    <button class="reg-theme-toggle" @click="toggleTheme" :title="darkMode?'Light mode':'Dark mode'">
      <i :class="darkMode?'mdi mdi-white-balance-sunny':'mdi mdi-moon-waning-crescent'"></i>
    </button>

    <div class="reg-wrapper">
      <div class="reg-card" :class="{ wide: step !== 4 }">
        <div class="text-center mb-4">
          <img :src="$brand.logo" :alt="$brand.name" class="reg-logo" @click="$router.push('/')" />
          <p class="reg-subtitle mt-2">Onboard your organisation onto {{ $brand.name }}</p>
        </div>

        <!-- Stepper -->
        <div v-if="step < 4" class="reg-stepper mb-4">
          <div class="reg-step" :class="{ active: step === 1, done: step > 1 }">
            <div class="reg-step-num"><i v-if="step > 1" class="mdi mdi-check"></i><span v-else>1</span></div>
            <div class="reg-step-label">Type</div>
          </div>
          <div class="reg-step-line" :class="{ done: step > 1 }"></div>
          <div class="reg-step" :class="{ active: step === 2, done: step > 2 }">
            <div class="reg-step-num"><i v-if="step > 2" class="mdi mdi-check"></i><span v-else>2</span></div>
            <div class="reg-step-label">Profile</div>
          </div>
          <div class="reg-step-line" :class="{ done: step > 2 }"></div>
          <div class="reg-step" :class="{ active: step === 3 }">
            <div class="reg-step-num"><span>3</span></div>
            <div class="reg-step-label">Admin</div>
          </div>
        </div>

        <!-- STEP 1: Type -->
        <div v-if="step === 1">
          <h5 class="text-center fw-bold mb-3 reg-title">What kind of organisation are you?</h5>
          <p class="text-center reg-step2-sub mb-4">This shapes the registration we'll ask you for — pick what fits best.</p>

          <div class="org-type-grid">
            <button v-for="t in orgTypes" :key="t.code"
                    class="org-type-card"
                    :class="{ selected: form.org_type === t.code }"
                    @click="form.org_type = t.code">
              <i :class="`mdi ${t.icon}`" class="org-type-icon"></i>
              <div class="org-type-name">{{ t.name }}</div>
              <div class="org-type-desc">{{ t.desc }}</div>
            </button>
          </div>

          <div v-if="errorMsg" class="alert reg-alert-danger py-2 px-3 mt-3"><i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}</div>

          <button class="btn btn-primary w-100 mt-4" @click="next" :disabled="!form.org_type">
            Continue <i class="mdi mdi-arrow-right ms-1"></i>
          </button>
          <div class="text-center mt-3 reg-secondary-text">
            Already onboarded? <a href="javascript:void(0)" @click="$router.push('/login')" class="reg-link fw-semibold">Sign in</a>
          </div>
        </div>

        <!-- STEP 2: Profile -->
        <div v-if="step === 2">
          <h5 class="text-center fw-bold mb-3 reg-title">{{ chosenTypeName }} details</h5>
          <p class="text-center reg-step2-sub mb-4">Tell us who you are. We verify all submissions before activation.</p>

          <!-- County dropdown (only for org_type=county) -->
          <div class="mb-3" v-if="form.org_type === 'county'">
            <label class="auth-label">Select your county <span class="text-danger">*</span></label>
            <select v-model="selectedCounty" @change="onCountyPicked" class="form-select auth-input">
              <option value="">— pick a county —</option>
              <option v-for="c in counties" :key="c.county_id" :value="c.county_id">
                {{ c.county_name }} ({{ c.county_code }})
              </option>
            </select>
            <div class="form-text reg-secondary-text" v-if="selectedCounty">
              Auto-filled: <strong>{{ form.org_name }}</strong> · code {{ form.registration_number }}
            </div>
          </div>

          <div class="mb-3" v-if="form.org_type !== 'county'">
            <label class="auth-label">{{ chosenTypeName }} name <span class="text-danger">*</span></label>
            <input v-model="form.org_name" type="text" class="form-control auth-input"
                   :placeholder="namePlaceholder" />
          </div>

          <div class="mb-3">
            <label class="auth-label">Legal / registered name (optional)</label>
            <input v-model="form.legal_name" type="text" class="form-control auth-input" placeholder="As on registration certificate" />
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="auth-label">KRA PIN</label>
              <input v-model="form.kra_pin" type="text" class="form-control auth-input" placeholder="A001234567X" />
            </div>
            <div class="col-md-6" v-if="form.org_type !== 'county'">
              <label class="auth-label">{{ regLabel }}</label>
              <input v-model="form.registration_number" type="text" class="form-control auth-input" :placeholder="regPlaceholder" />
            </div>
            <div class="col-md-6" v-else>
              <label class="auth-label">County code</label>
              <input v-model="form.registration_number" type="text" class="form-control auth-input" readonly />
            </div>
          </div>

          <hr class="my-3">
          <h6 class="reg-section-h">Primary contact</h6>

          <div class="mb-3">
            <label class="auth-label">Contact name <span class="text-danger">*</span></label>
            <input v-model="form.contact_name" type="text" class="form-control auth-input" placeholder="Full name of person we should call" />
          </div>

          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="auth-label">Email <span class="text-danger">*</span></label>
              <input v-model="form.contact_email" type="email" class="form-control auth-input" placeholder="contact@example.org" />
            </div>
            <div class="col-md-6">
              <label class="auth-label">Phone <span class="text-danger">*</span></label>
              <input v-model="form.contact_phone" type="tel" class="form-control auth-input" placeholder="+254 7XX XXX XXX" />
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Address</label>
            <input v-model="form.address" type="text" class="form-control auth-input" placeholder="PO Box, town, county" />
          </div>

          <div v-if="errorMsg" class="alert reg-alert-danger py-2 px-3"><i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}</div>

          <div class="d-flex gap-2 mt-3">
            <button class="btn btn-outline-secondary" @click="step = 1; errorMsg = ''">
              <i class="mdi mdi-arrow-left me-1"></i> Back
            </button>
            <button class="btn btn-primary flex-grow-1" @click="next" :disabled="!canProceedProfile">
              Continue <i class="mdi mdi-arrow-right ms-1"></i>
            </button>
          </div>
        </div>

        <!-- STEP 3: Admin -->
        <div v-if="step === 3">
          <h5 class="text-center fw-bold mb-3 reg-title">Your admin account</h5>
          <p class="text-center reg-step2-sub mb-4">You'll be the first administrator for <strong>{{ form.org_name }}</strong>.<br>
            You cannot sign in until {{ $brand.name }} reviews + approves your application.</p>

          <div class="row g-3 mb-3">
            <div class="col-md-6">
              <label class="auth-label">First name <span class="text-danger">*</span></label>
              <input v-model="form.admin_first_name" type="text" class="form-control auth-input" />
            </div>
            <div class="col-md-6">
              <label class="auth-label">Last name</label>
              <input v-model="form.admin_last_name" type="text" class="form-control auth-input" />
            </div>
          </div>

          <div class="mb-3">
            <label class="auth-label">Username <span class="text-danger">*</span></label>
            <input v-model="form.admin_username" type="text" class="form-control auth-input" placeholder="e.g. john.sankale" autocomplete="username" />
          </div>

          <div class="mb-3">
            <label class="auth-label">Password <span class="text-danger">*</span></label>
            <div class="input-group">
              <input :type="showPw?'text':'password'" v-model="form.admin_password" class="form-control auth-input" placeholder="At least 8 characters" autocomplete="new-password" />
              <button type="button" class="input-group-text password-toggle" @click="showPw = !showPw">
                <i :class="showPw?'mdi mdi-eye-off':'mdi mdi-eye'"></i>
              </button>
            </div>
            <div class="mt-1" style="font-size:12px;color:#64748b;line-height:1.45;">
              Must be at least 8 characters.
              <span :class="form.admin_password.length >= 8 ? 'text-success' : ''" v-if="form.admin_password">
                <i class="mdi" :class="form.admin_password.length >= 8 ? 'mdi-check-circle' : 'mdi-progress-clock'"></i>
                {{ form.admin_password.length }}/8
              </span>
              <br>
              Recommended: mix upper + lower case, a number and a symbol.
            </div>
          </div>

          <div class="form-check mb-3">
            <input v-model="form.agreeTos" type="checkbox" class="form-check-input" id="agreeTos">
            <label class="form-check-label reg-secondary-text" for="agreeTos">
              I confirm the information above is accurate and authorise {{ $brand.name }} to verify it against KMHFL / NGO Board / KRA records.
            </label>
          </div>

          <div v-if="errorMsg" class="alert reg-alert-danger py-2 px-3"><i class="mdi mdi-alert-circle me-1"></i> {{ errorMsg }}</div>

          <div class="d-flex gap-2 mt-3">
            <button class="btn btn-outline-secondary" @click="step = 2; errorMsg = ''" :disabled="submitting">
              <i class="mdi mdi-arrow-left me-1"></i> Back
            </button>
            <button class="btn btn-primary flex-grow-1" @click="submit" :disabled="!canSubmit || submitting">
              <span v-if="submitting"><span class="spinner-border spinner-border-sm me-1"></span> Submitting…</span>
              <span v-else><i class="mdi mdi-send me-1"></i> Submit application</span>
            </button>
          </div>
        </div>

        <!-- STEP 4: Success -->
        <div v-if="step === 4">
          <div class="text-center">
            <div class="reg-success-icon"><i class="mdi mdi-clock-outline"></i></div>
            <h5 class="fw-bold reg-title">Application received!</h5>
            <p class="reg-success-sub">
              Thanks {{ form.admin_first_name }}. Your application for <strong>{{ form.org_name }}</strong> is now in the {{ $brand.name }} review queue.<br><br>
              Reference code: <code class="reg-code">{{ result.org_code }}</code><br><br>
              We'll email <strong>{{ form.contact_email }}</strong> within 1–2 business days once it's reviewed. You cannot sign in until then.
            </p>
            <button class="btn btn-primary w-100 mt-3" @click="$router.push('/login')">
              <i class="mdi mdi-login me-1"></i> Go to Sign In
            </button>
            <button class="btn btn-link w-100 mt-2 reg-secondary-text" @click="$router.push('/')">
              Back to home
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from "axios";
import { hms } from "@/api";

export default {
  name: "OrgSignup",
  data() {
    return {
      darkMode: false,
      step: 1,
      submitting: false,
      showPw: false,
      errorMsg: "",
      result: {},
      counties: [],
      selectedCounty: "",
      orgTypes: [
        { code: "county",        name: "County government",    icon: "mdi-domain",          desc: "A devolved Kenyan county onboarding all its public facilities." },
        { code: "ngo",           name: "NGO / faith-based",    icon: "mdi-account-heart",   desc: "A non-profit running clinics or hospitals across one or more counties." },
        { code: "private_group", name: "Private hospital group", icon: "mdi-hospital-building", desc: "A private group with multiple branches (e.g. Aga Khan, Nairobi Hospital)." },
        { code: "individual",    name: "Individual / single facility", icon: "mdi-account-circle", desc: "A doctor or owner running one clinic or hospital." },
      ],
      form: {
        org_type: "",
        org_name: "",
        legal_name: "",
        kra_pin: "",
        registration_number: "",
        contact_name: "",
        contact_email: "",
        contact_phone: "",
        address: "",
        admin_first_name: "",
        admin_last_name: "",
        admin_username: "",
        admin_password: "",
        agreeTos: false,
      }
    };
  },
  computed: {
    chosenTypeName() {
      const t = this.orgTypes.find(x => x.code === this.form.org_type);
      return t ? t.name : "Organisation";
    },
    namePlaceholder() {
      switch (this.form.org_type) {
        case "county":        return "e.g. Narok County Government";
        case "ngo":           return "e.g. AMREF Health Africa";
        case "private_group": return "e.g. Aga Khan Health Services";
        default:              return "e.g. Smith Family Medical Clinic";
      }
    },
    regLabel() {
      switch (this.form.org_type) {
        case "county":        return "County code";
        case "ngo":           return "NGO Board registration no.";
        case "private_group": return "Company registration no.";
        default:              return "KMPDC / facility code";
      }
    },
    regPlaceholder() {
      switch (this.form.org_type) {
        case "county":        return "e.g. 033 (Narok)";
        case "ngo":           return "e.g. NGO-2024-XXXXX";
        case "private_group": return "e.g. PVT-XXXXX";
        default:              return "e.g. KMPDC No.";
      }
    },
    canProceedProfile() {
      return this.form.org_name.trim() && this.form.contact_name.trim()
          && this.form.contact_email.trim() && this.form.contact_phone.trim();
    },
    canSubmit() {
      return this.form.admin_first_name.trim() && this.form.admin_username.trim()
          && this.form.admin_password.length >= 8 && this.form.agreeTos;
    }
  },
  async mounted() {
    // Preload counties so they're ready when the user picks "county" org_type
    try {
      const p = new URLSearchParams(); p.append("function", "listCountiesPublic");
      const r = await axios.post(hms + "?function=listCountiesPublic", p);
      if (r.data && r.data.success) this.counties = r.data.data.rows || [];
    } catch (e) { /* silent — dropdown just falls back to manual entry */ }
  },
  methods: {
    toggleTheme() {
      this.darkMode = !this.darkMode;
      document.body.classList.toggle("dark-mode", this.darkMode);
    },
    onCountyPicked() {
      const c = this.counties.find(x => String(x.county_id) === String(this.selectedCounty));
      if (!c) return;
      this.form.org_name = c.county_name + " County Government";
      this.form.registration_number = c.county_code || "";
    },
    next() {
      this.errorMsg = "";
      if (this.step === 1 && !this.form.org_type) {
        this.errorMsg = "Please pick an organisation type.";
        return;
      }
      if (this.step === 2 && !this.canProceedProfile) {
        this.errorMsg = "All required fields must be filled in.";
        return;
      }
      this.step++;
    },
    async submit() {
      if (!this.canSubmit) return;
      this.errorMsg = "";
      this.submitting = true;
      try {
        const params = new URLSearchParams();
        params.append("function", "signupOrganization");
        Object.entries(this.form).forEach(([k, v]) => {
          if (k === "agreeTos") return;
          params.append(k, v);
        });
        const res = await axios.post(hms + "?function=signupOrganization", params);
        if (res.data && res.data.success) {
          this.result = res.data.data || {};
          this.step = 4;
        } else {
          this.errorMsg = (res.data && res.data.message) || "Submission failed. Please try again.";
        }
      } catch (e) {
        this.errorMsg = (e.response && e.response.data && e.response.data.message) || "Network error. Please try again.";
      } finally {
        this.submitting = false;
      }
    }
  }
};
</script>

<style scoped>
.reg-root { background: var(--hms-bg2)!important; min-height: 100vh; position: relative; }
.reg-theme-toggle { position: fixed; top: 16px; right: 16px; z-index: 10; width: 40px; height: 40px; border-radius: 50%; background: var(--hms-bg3); border: 1px solid var(--hms-br); color: var(--hms-primary); cursor: pointer; font-size: 18px; display: flex; align-items: center; justify-content: center; transition: all .2s; }
.reg-theme-toggle:hover { transform: scale(1.05); }
.reg-wrapper { display: flex; align-items: center; justify-content: center; min-height: 100vh; padding: 20px; }
.reg-card { background: var(--hms-bg1)!important; border: 1px solid var(--hms-br); border-radius: 16px; padding: 36px 32px; width: 100%; max-width: 520px; box-shadow: 0 12px 40px rgba(0,0,0,.06); }
.reg-card.wide { max-width: 640px; }
.reg-logo { height: 52px; cursor: pointer; }
.reg-subtitle { color: var(--hms-s); font-size: 13px; }
.reg-title { color: var(--hms-h); }
.reg-step2-sub { color: var(--hms-m); font-size: 13px; }
.reg-section-h { color: var(--hms-h); font-size: 13px; text-transform: uppercase; letter-spacing: .5px; margin-bottom: 12px; }

/* Stepper */
.reg-stepper { display: flex; align-items: center; justify-content: center; gap: 0; }
.reg-step { display: flex; flex-direction: column; align-items: center; gap: 6px; }
.reg-step-num { width: 32px; height: 32px; border-radius: 50%; background: var(--hms-bg3); border: 2px solid var(--hms-br); color: var(--hms-s); display: flex; align-items: center; justify-content: center; font-weight: 700; font-size: 13px; transition: all .2s; }
.reg-step.active .reg-step-num { background: var(--hms-primary); border-color: var(--hms-primary); color: #fff; }
.reg-step.done .reg-step-num { background: var(--hms-success, #10b981); border-color: var(--hms-success, #10b981); color: #fff; }
.reg-step-label { font-size: 11px; color: var(--hms-s); font-weight: 600; }
.reg-step-line { width: 50px; height: 2px; background: var(--hms-br); margin: 0 8px; align-self: flex-start; margin-top: 16px; }
.reg-step-line.done { background: var(--hms-success, #10b981); }

/* Type cards */
.org-type-grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 12px; }
.org-type-card { background: var(--hms-bg2); border: 2px solid var(--hms-br); border-radius: 12px; padding: 18px 14px; cursor: pointer; text-align: center; transition: all .15s; color: var(--hms-h); }
.org-type-card:hover { border-color: var(--hms-primary); transform: translateY(-2px); }
.org-type-card.selected { border-color: var(--hms-primary); background: var(--hms-primary-lt, rgba(59,130,246,.08)); box-shadow: 0 4px 12px rgba(59,130,246,.18); }
.org-type-icon { font-size: 32px; color: var(--hms-primary); display: block; margin-bottom: 6px; }
.org-type-name { font-weight: 700; font-size: 14px; margin-bottom: 4px; color: var(--hms-h); }
.org-type-desc { font-size: 11px; color: var(--hms-s); line-height: 1.35; }

.auth-label { font-size: 12px; font-weight: 600; color: var(--hms-h); margin-bottom: 4px; display: block; }
.auth-input { background: var(--hms-bg2); border: 1px solid var(--hms-br); color: var(--hms-h); }
.auth-input:focus { background: var(--hms-bg2); border-color: var(--hms-primary); box-shadow: 0 0 0 .2rem var(--hms-primary-lt, rgba(59,130,246,.18)); color: var(--hms-h); }
.password-toggle { background: var(--hms-bg3); border: 1px solid var(--hms-br); cursor: pointer; color: var(--hms-s); }

.reg-alert-danger { background: rgba(239,68,68,.08); border: 1px solid rgba(239,68,68,.3); color: #ef4444; border-radius: 8px; font-size: 13px; }
.reg-secondary-text { color: var(--hms-s); font-size: 13px; }
.reg-link { color: var(--hms-primary); text-decoration: none; }
.reg-link:hover { text-decoration: underline; }

.reg-success-icon { font-size: 64px; color: var(--hms-warning, #f59e0b); margin-bottom: 12px; }
.reg-success-sub { color: var(--hms-m); font-size: 14px; line-height: 1.6; }
.reg-code { background: var(--hms-bg3); padding: 4px 10px; border-radius: 6px; color: var(--hms-primary); font-weight: 700; font-size: 14px; }

@media (max-width: 540px) {
  .org-type-grid { grid-template-columns: 1fr; }
  .reg-card { padding: 24px 20px; }
}
</style>
